import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Table, Modal, ModalHeader, ModalBody } from "reactstrap";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import ConfirmationModal from "components/common/ConfirmationMessage";
import { convertTimeToUTC } from "helpers/convertTimeToUTC";
import { apReportSelector, fetchApReports, markReportAsPaid, deleteApReport, downloadApReportFile } from "slices/apReportSlice";
import { downloadConsolidatedInvoice } from "slices/apReportSlice";
import moment from "moment";
import { usePermissions } from "layouts/Admin";

function ApReportList() {

  const dispatch = useDispatch();
  const { reports, last_page: totalPages, loader, total, current_page, per_page: pageLength } =
    useSelector(apReportSelector);

  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, id: null, action: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    sortBy: "created_at",
    sortDirection: "desc",
  });

  useEffect(() => {
    console.log("🚀 ApReportList mounted");
    dispatch(fetchApReports(1));
  }, []);

  const { checkPermission } = usePermissions();
  const renderTableHeader = useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      dispatch(fetchApReports(1, { sortBy, sortDirection: dir, search: searchTerm }));
    };

    return (
      <tr>
        <th className="col-1">
          <TableHeaderItem title="ID" sortBy="id" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th className='col-2'>Invoices</th>
        <th className='col-2'>
          <TableHeaderItem title="Created By" sortBy="created_by" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th className='col-2'>
          <TableHeaderItem title="Paid At" sortBy="paid_at" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th className='col-2'>
          <TableHeaderItem title="Paid By" sortBy="paid_by" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th className='col-2'>
          <TableHeaderItem title="Creation Date" sortBy="created_at" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        {(checkPermission('Paid Ap Reports') || checkPermission('Download Ap Reports') || checkPermission('Delete Ap Reports')) && (
          <th className='col-2'>Actions</th>
        )}
      </tr>
    );
  }, [sortConfig, searchTerm]);

  // ===== TABLE BODY =====
  const renderTableContent = useCallback(() => {
    return (
      reports &&
      reports.map((report, index) => (
        <ReportRow
          key={report.id}
          report={report}
          index={index}
          current_page={current_page}
          pageLength={pageLength}
          onPaidClick={(id) => setConfirmModal({ show: true, id, action: 'paid' })}
          onDeleteClick={(id) => setConfirmModal({ show: true, id, action: 'delete' })}
          onFileDownload={(id, type, filename) => dispatch(downloadApReportFile(id, type, filename))}
        />
      ))
    );
  }, [reports, current_page, pageLength]);

  // ===== Confirm dialog (delete / mark paid) =====
  const handleConfirm = () => {
    const { id, action } = confirmModal;
    setConfirmModal({ show: false, id: null, action: null });
    const callback = (success, message) => {
      if (success) {
        toast.success(message, { className: "successToast" });
        dispatch(fetchApReports(current_page));
      } else {
        toast.error(message, { className: "errorToast" });
      }
    };
    dispatch(action === 'paid' ? markReportAsPaid(id, callback) : deleteApReport(id, callback));
  };

  return (
    <ListLayout
      heading="AP Reports"
      title=""
      renderTableHeader={renderTableHeader}
      renderTableContent={renderTableContent}
      totalPages={totalPages}
      fetchList={fetchApReports}
      selectedItems={selectedItems}
      addButtonDisabled={true}
      addformModal={false}
      renderAddLink={(callback) => ''}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      loader={loader}
      searchPlaceholder="Search AP Reports..."
      noOfItems={total}
      disableBootstrap={true}
    >
      {confirmModal.show && (
        <ConfirmationModal
          isOpen={confirmModal.show}
          onClose={() => setConfirmModal({ show: false, id: null, action: null })}
          onConfirm={handleConfirm}
          message={confirmModal.action === 'paid'
            ? 'Mark this AP report as paid? Interpreters on it will be emailed a payment notification.'
            : 'Are you sure you want to delete this AP report? Unpaid jobs on it will appear on the next report run.'}
        />
      )}
    </ListLayout>
  );
}

// Icon-only action button (the row uses FA icons instead of labeled buttons)
const iconBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1.15rem',
  padding: '4px 7px',
};

const ReportRow = ({ report, index, current_page, pageLength, onPaidClick, onDeleteClick, onFileDownload }) => {
  const { checkPermission } = usePermissions();

  const dispatch = useDispatch();

  const [showInvoices, setShowInvoices] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const handleDownload = async () => {
  dispatch(downloadConsolidatedInvoice(report.id));
};

  // A batch is a non-contiguous SET of invoices (old unpaid ones sit beside new ones), so the
  // from–to range is only a min/max bound; the expandable list is the authoritative set.
  const invoiceCount = report?.invoice_count ?? null;
  const invoiceNumbers = report?.invoice_numbers
    ? String(report.invoice_numbers).split(',').filter(Boolean)
    : [];
  const invoiceRange = report?.invoice_from_number
    ? (report.invoice_from_number === report.invoice_to_number
        ? report.invoice_from_number
        : `${report.invoice_from_number}–${report.invoice_to_number}`)
    : null;

  return (
    <>
    <tr>
      <td>AP{report?.id}</td>
      <td>
        {invoiceCount ? (
          <>
            <button
              type="button"
              style={{ ...iconBtn, fontSize: '0.85rem', padding: 0, textAlign: 'left' }}
              title={showInvoices ? 'Hide invoice numbers' : 'Show all invoice numbers in this batch'}
              onClick={() => setShowInvoices((v) => !v)}
            >
              <span style={{ fontWeight: 600 }}>{invoiceCount}</span>
              {invoiceRange && <span style={{ color: '#8898aa' }}> · {invoiceRange}</span>}
              <i className={`fa-solid ${showInvoices ? 'fa-chevron-up' : 'fa-chevron-down'} ms-1`} style={{ fontSize: '0.7rem' }}></i>
            </button>
            {showInvoices && (
              <div style={{ fontSize: '0.8rem', color: '#525f7f', marginTop: 3, wordBreak: 'break-all', maxWidth: 260 }}>
                {invoiceNumbers.join(', ')}
              </div>
            )}
          </>
        ) : (
          <span style={{ color: '#8898aa' }}>-</span>
        )}
      </td>
      <td>{report?.created_by_user?.username ?? report?.created_by_user?.name}</td>
      <td>{report?.paid_at ? moment(report.paid_at).format('YYYY-MM-DD h:mm A') : '-'}</td>
      <td>{report?.paid_by_user?.username ?? report?.paid_by_user?.name ?? "-"}</td>
      <td>{report?.created_at ? moment(report.created_at).format('YYYY-MM-DD h:mm A') : '-'}</td>
      {(checkPermission('Paid Ap Reports') || checkPermission('Download Ap Reports') || checkPermission('Delete Ap Reports')) && (
      <td className="d-flex align-items-center justify-content-end gap-1">
        {invoiceCount > 0 && (
        <button type="button" style={iconBtn} title="View invoices in this batch" onClick={() => setInvoiceModalOpen(true)}>
          <i className="fa-solid fa-list-ul" style={{ color: '#5e72e4' }}></i>
        </button>)}
        {report?.paid !== 1 && checkPermission('Paid Ap Reports') && (
        <button type="button" style={iconBtn} title="Mark as paid" onClick={() => onPaidClick(report?.id)}>
          <i className="fa-solid fa-circle-check" style={{ color: '#2dce89' }}></i>
        </button>)}
        {checkPermission('Download Ap Reports') && (
        <>
          {report?.csv_file_path && (
          <button type="button" style={iconBtn} title="Download CSV" onClick={() => onFileDownload(report?.id, 'csv', `AP_Report_${report?.id}.csv`)}>
            <i className="fa-solid fa-file-csv" style={{ color: '#2dce89' }}></i>
          </button>)}
          {report?.detail_ap_file_path && (
          <button type="button" style={iconBtn} title="Download Details PDF" onClick={() => onFileDownload(report?.id, 'detail', `AP_Report_Details_${report?.id}.pdf`)}>
            <i className="fa-solid fa-file-pdf" style={{ color: '#f5365c' }}></i>
          </button>)}
          {report?.file_path && (
          <button type="button" style={iconBtn} title="Download Invoice PDF" onClick={() => onFileDownload(report?.id, 'invoice', `Invoice_${report?.id}.pdf`)}>
            <i className="fa-solid fa-file-invoice-dollar" style={{ color: '#5e72e4' }}></i>
          </button>)}
          <button type="button" style={iconBtn} title="Download all files (.zip)" onClick={handleDownload}>
            <i className="fa-solid fa-file-zipper" style={{ color: '#8898aa' }}></i>
          </button>
        </>)}
        {/* Paid runs are the money trail — only unpaid duplicates can be trashed */}
        {report?.paid !== 1 && checkPermission('Delete Ap Reports') && (
        <button type="button" style={iconBtn} title="Delete report" onClick={() => onDeleteClick(report?.id)}>
          <i className="fa-solid fa-trash-can" style={{ color: '#f5365c' }}></i>
        </button>)}
      </td>)}
    </tr>

    <Modal isOpen={invoiceModalOpen} toggle={() => setInvoiceModalOpen(false)} centered scrollable>
      <ModalHeader toggle={() => setInvoiceModalOpen(false)}>
        Invoices in AP{report?.id}
      </ModalHeader>
      <ModalBody>
        <div className="d-flex flex-wrap gap-3 mb-3" style={{ fontSize: '0.9rem' }}>
          <div><span style={{ color: '#8898aa' }}>Count:</span> <strong>{invoiceCount}</strong></div>
          {invoiceRange && (
          <div><span style={{ color: '#8898aa' }}>Range:</span> <strong>{invoiceRange}</strong></div>)}
          {report?.invoice_date_from && (
          <div><span style={{ color: '#8898aa' }}>Dates:</span> <strong>{report.invoice_date_from} – {report.invoice_date_to}</strong></div>)}
        </div>
        {/* A batch is a non-contiguous SET (it sweeps up every unpaid invoice), so the range is
            only a min/max bound — this is the authoritative full list for reconciliation. */}
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          <div className="d-flex flex-wrap gap-2">
            {invoiceNumbers.map((num) => (
              <span key={num} style={{ background: '#f6f9fc', border: '1px solid #e9ecef', borderRadius: 4, padding: '2px 8px', fontSize: '0.85rem' }}>{num}</span>
            ))}
          </div>
        </div>
      </ModalBody>
    </Modal>
    </>
  );
};

export default ApReportList;
